// Worker page section: kind state (P2-H02): pause, breaker, boot-disabled
// reason (e.g. self-test-pending, SQ-22), requeue_safe and money, with
// operator pause/unpause (P2-H03). Unpause is also the W1 staged unpause of
// boot-paused money kinds (C-14).
import { useState } from 'react';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@heyroot/ui/primitives/Table';
import { PERMS } from '../../../../rbac/permissions';
import {
  pauseWorkerKind,
  unpauseWorkerKind,
  useWorkerAction,
  useWorkerKinds,
} from '../../../lib/worker-api';
import type { WorkerSectionWeb } from '../sections';
import {
  ActionOutcome,
  PauseBadge,
  PauseForm,
  QueryState,
  SectionHeading,
  SeverityBadge,
  fmtPauseEnd,
  fmtTs,
  useCanOperate,
} from '../worker-ui';

function KindsSection() {
  const q = useWorkerKinds();
  const canOperate = useCanOperate();
  const [pausing, setPausing] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const pause = useWorkerAction((kind: string, until: string, reason: string) =>
    pauseWorkerKind(kind, until, reason),
  );
  const unpause = useWorkerAction((kind: string) => unpauseWorkerKind(kind));
  const lastError = pause.error ?? unpause.error;
  const busy = pause.isPending || unpause.isPending;
  const rows = q.data?.kinds ?? [];

  return (
    <section className="flex flex-col gap-3" aria-label="Kinds">
      <SectionHeading
        title="Kinds"
        description="Pause, breaker and boot-disabled state per job kind. A pause counts only until its end time."
      />
      <ActionOutcome error={lastError} success={lastError ? null : success} />
      <QueryState
        isPending={q.isPending}
        isError={q.isError}
        isEmpty={rows.length === 0}
        emptyTitle="No kinds are registered"
        onRetry={() => void q.refetch()}
      >
        <div className="overflow-x-auto">
          <Table dense>
            <TableHead>
              <tr>
                <TableHeaderCell>Kind</TableHeaderCell>
                <TableHeaderCell>Severity</TableHeaderCell>
                <TableHeaderCell>Queued / running</TableHeaderCell>
                <TableHeaderCell>Failures</TableHeaderCell>
                <TableHeaderCell>State</TableHeaderCell>
                <TableHeaderCell>Boot</TableHeaderCell>
                {canOperate && <TableHeaderCell>Actions</TableHeaderCell>}
              </tr>
            </TableHead>
            <TableBody>
              {rows.map((k) => (
                <TableRow key={k.kind}>
                  <TableCell>
                    <div className="text-body-sm-emphasis">{k.kind}</div>
                    <div className="text-caption text-muted">
                      {k.lane ?? 'drainer'} · {k.alertOwner}
                    </div>
                  </TableCell>
                  <TableCell>
                    <SeverityBadge severity={k.severity} />
                  </TableCell>
                  <TableCell>
                    {k.queued} / {k.running}
                  </TableCell>
                  <TableCell>{k.consecutiveFailures}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 flex-wrap">
                      <PauseBadge pausedNow={k.pausedNow} reason={k.pausedReason} />
                      {k.pausedNow && (
                        <span className="text-caption text-muted">
                          {fmtPauseEnd(k.pausedUntil)}
                        </span>
                      )}
                      {k.money && <Badge variant="info">money</Badge>}
                      {k.requeueSafe && <Badge variant="success">requeue-safe</Badge>}
                      {k.retiredAt && <Badge variant="neutral">retired</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {k.bootDisabledReason ? (
                      <Badge variant="danger" title={`since ${fmtTs(k.bootDisabledAt)}`}>
                        {k.bootDisabledReason}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  {canOperate && (
                    <TableCell>
                      {pausing === k.kind ? (
                        <PauseForm
                          target={k.kind}
                          busy={busy}
                          onCancel={() => setPausing(null)}
                          onPause={(until, reason) =>
                            pause.mutate([k.kind, until, reason], {
                              onSuccess: (res) => setSuccess(`Pause ${k.kind}: ${res.result}`),
                              onSettled: () => setPausing(null),
                            })
                          }
                        />
                      ) : k.pausedNow ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          onClick={() =>
                            unpause.mutate([k.kind], {
                              onSuccess: (res) => setSuccess(`Unpause ${k.kind}: ${res.result}`),
                            })
                          }
                        >
                          Unpause
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          onClick={() => setPausing(k.kind)}
                        >
                          Pause
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </QueryState>
    </section>
  );
}

export const section: WorkerSectionWeb = {
  key: 'kinds',
  order: 30,
  label: 'Kinds',
  perm: PERMS.WORKER_READ,
  Component: KindsSection,
};
